import { create } from "zustand";
import { send_request } from "../../utils/Request";
import {
  extractMessageAddedChatMessage,
  extractTaskQueueEvent,
  getSseEventPayload,
  getSseEventType,
  isTaskQueueSseEvent,
} from "../../utils/ChatEventReducer";
import { createStreamController } from "./conversation/stream/streamController";
import { reduceTeamActivity } from "./conversation/stream/teamActivityReducer";
import { getTeamChildSession } from "../../api/teamsApi";
import useChatStreamStore from "./useChatStreamStore";
import usePublicChatStore from "./usePublicChatStore";
import useTaskQueueStore from "./useTaskQueueStore";
import config from "../../config";
import useProjectStore from "../ProjectCanvas/useProjectStore";
import {
  log as _log,
  critical as _crit,
  findUnresolvedStreamParts,
  findIncompleteToolParts,
  summarizeUnresolvedStreamParts,
  summarizeIncompleteTools,
  summarizeMessages,
  summarizeParts,
} from "../../utils/ChatStreamLogger";
import { stopManagedRun as stopManagedRunRequest } from "./ManagedRunPolicy/managedRunApi";
import {
  isSessionRowMetadataPending,
  normalizeSessionListSenders,
} from "./agentSessionsPanelUtils";
import { sortSessionMessages } from "./conversation/chatMessageOrder";
import {
  applySessionDetailToList,
  buildOptimisticSessionListRow,
  reconcileFetchedSessionList,
  upsertSessionListRow,
} from "./sessionListOptimistic";

function _getClientId() {
  return useProjectStore.getState().clientId;
}

const STATUS_WAITING_FOR_REVIEW = "WAITING_FOR_REVIEW";
const STATUS_SESSION_UNDER_REVIEW = "SESSION_UNDER_REVIEW";
const STATUS_IN_PROGRESS = "IN_PROGRESS";
const STATUS_WAITING_FOR_WORKERS = "WAITING_FOR_WORKERS";
const STATUS_WAITING_FOR_SCHEDULED_MESSAGE = "WAITING_FOR_SCHEDULED_MESSAGE";
const STATUS_WAITING_FOR_FORM_SUBMISSION = "WAITING_FOR_FORM_SUBMISSION";
const STATUS_CANCELLING = "CANCELLING";
const DEFAULT_BLOCKED_WHILE_REVIEW_MESSAGE =
  "This conversation is currently under review. Please wait for the reviewer to finish.";

function parseMaybeJsonPayload(payload) {
  if (!payload) return null;
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch (_error) {
      return null;
    }
  }
  if (typeof payload === "object") {
    return payload;
  }
  return null;
}

function normalizeSessionStatusReason(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function extractApiStatusCode(responseOrError) {
  if (!responseOrError || typeof responseOrError !== "object") {
    return 0;
  }
  return Number(
    responseOrError?.status || responseOrError?.response?.status || 0
  );
}

function extractApiPayload(responseOrError) {
  if (!responseOrError || typeof responseOrError !== "object") {
    return null;
  }
  const rawPayload = responseOrError?.data ?? responseOrError?.response?.data;
  return parseMaybeJsonPayload(rawPayload);
}

function resolveApiErrorMessage(responseOrError, fallbackMessage) {
  const payload = extractApiPayload(responseOrError);
  if (payload && typeof payload === "object") {
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error.trim();
    }
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message.trim();
    }
  }
  if (
    typeof responseOrError?.message === "string" &&
    responseOrError.message.trim()
  ) {
    return responseOrError.message.trim();
  }
  return fallbackMessage;
}

function inferMimeTypeFromFileName(fileName) {
  if (typeof fileName !== "string") return "text/plain";
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".md")) return "text/markdown";
  if (lower.endsWith(".txt")) return "text/plain";
  if (lower.endsWith(".json")) return "application/json";
  if (lower.endsWith(".csv")) return "text/csv";
  return "text/plain";
}

function extractBlockedIngressPayload(responseOrError) {
  const statusCode = extractApiStatusCode(responseOrError);
  if (statusCode !== 409) {
    return null;
  }
  const payload = extractApiPayload(responseOrError);
  if (!payload || payload.status !== STATUS_SESSION_UNDER_REVIEW) {
    return null;
  }
  return {
    chatSessionId: String(payload.chatSessionId || ""),
    sessionStatus: String(payload.sessionStatus || STATUS_WAITING_FOR_REVIEW),
    message:
      typeof payload.message === "string" && payload.message.trim()
        ? payload.message.trim()
        : DEFAULT_BLOCKED_WHILE_REVIEW_MESSAGE,
  };
}

const _TERMINAL_WORKER_STATUSES = new Set(["COMPLETED", "CANCELLED", "ERROR"]);
const _STREAMABLE_SESSION_STATUSES = new Set([
  STATUS_IN_PROGRESS,
  STATUS_WAITING_FOR_WORKERS,
  STATUS_WAITING_FOR_SCHEDULED_MESSAGE,
  STATUS_WAITING_FOR_FORM_SUBMISSION,
  STATUS_WAITING_FOR_REVIEW,
  // 409 blocked-ingress flows (extractBlockedIngressPayload) produce SESSION_UNDER_REVIEW
  // as a non-terminal "under review" state; treat it as streamable so SSE reconnect/processing
  // continues until the session leaves review.
  STATUS_SESSION_UNDER_REVIEW,
]);

const _ACTIVE_RUN_EVENT_TYPES = new Set([
  "tool.invoked",
  "tool.completed",
  "tool.failed",
  "tool_activity",
  "worker_status_changed",
]);

function _isStreamableSessionStatus(status) {
  return !status || _STREAMABLE_SESSION_STATUSES.has(status);
}

function _parseSessionActivityMs(session) {
  const raw = session?.updatedAt ?? session?.createdAt;
  if (!raw) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function _resolveSessionTeamId(session) {
  return session?.teamId || session?.scopedTeamId || null;
}

function _buildAgentActivityFromSessions(sessions) {
  const activeMap = {};
  const lastActivityMap = {};
  const teamLastActivityMap = {};
  for (const session of sessions) {
    const updatedAtMs = _parseSessionActivityMs(session);
    const teamId = _resolveSessionTeamId(session);
    if (teamId && updatedAtMs > 0) {
      const prevTeam = teamLastActivityMap[teamId] || 0;
      if (updatedAtMs > prevTeam) {
        teamLastActivityMap[teamId] = updatedAtMs;
      }
    }
    if (!session?.agentId) continue;
    if (updatedAtMs > 0) {
      const prev = lastActivityMap[session.agentId] || 0;
      if (updatedAtMs > prev) {
        lastActivityMap[session.agentId] = updatedAtMs;
      }
    }
    if (_isStreamableSessionStatus(session.status)) {
      activeMap[session.agentId] = true;
    }
  }
  return { activeMap, lastActivityMap, teamLastActivityMap };
}

function _bumpTeamLastActivityPatch(state, teamId, timestampMs = Date.now()) {
  const prev = state.teamLastActivityAt?.[teamId] || 0;
  const next = Math.max(prev, timestampMs);
  if (next === prev) return null;
  return {
    teamLastActivityAt: { ...state.teamLastActivityAt, [teamId]: next },
  };
}

function _activityMsFromPayload(payload) {
  const inner = payload?.payload ?? payload;
  const raw = inner?.updatedAt ?? inner?.timestamp;
  if (raw) {
    const ms = new Date(raw).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  return Date.now();
}

function _bumpAgentLastActivityPatch(state, agentId, timestampMs = Date.now()) {
  const prev = state.agentLastActivityAt?.[agentId] || 0;
  const next = Math.max(prev, timestampMs);
  if (next === prev) return null;
  return {
    agentLastActivityAt: { ...state.agentLastActivityAt, [agentId]: next },
  };
}

function _isTerminalForStreaming(status) {
  return Boolean(status) && !_isStreamableSessionStatus(status) && status !== STATUS_CANCELLING;
}

function _isKnownStreamableSessionStatus(status) {
  return Boolean(status) && _isStreamableSessionStatus(status);
}

function _isActivelyRunningSessionStatus(status) {
  return status === STATUS_IN_PROGRESS;
}

const _BUSY_CHAT_STATUSES = new Set(["submitted", "streaming", "error"]);

// Single rule for "a freshly observed terminal backend status must clear a busy
// chatStatus". session_complete is the normal clear, but it is unrecoverable
// once missed (the backend purges its replay buffer), so REST/poll fetches and
// mid-stream snapshots funnel their terminal observations through this same rule
// as the backstop against a permanently stuck "Thinking". Returns the reason
// string to record before clearing to 'ready', or null when no clear is
// warranted.
function _terminalClearReason(observedStatus, chatStatus, source) {
  if (!_isTerminalForStreaming(observedStatus)) return null;
  if (!_BUSY_CHAT_STATUSES.has(chatStatus)) return null;
  return `${source}:terminal=${observedStatus}`;
}

function _markSubmittedStreamStarted(reason, getStore, setStore) {
  if (getStore().chatStatus !== 'submitted') return;
  _clearSubmittedRecovery();
  _markChatStatusReason(reason);
  setStore({ chatStatus: 'streaming' });
}

function _hasIncompleteWorkers(workerStatuses) {
  if (!workerStatuses) return false;
  return Object.values(workerStatuses).some(
    (w) => w?.status && !_TERMINAL_WORKER_STATUSES.has(w.status)
  );
}

// True while any team member node is still running, so the parent SSE stream is
// kept alive across team turns even when the parent session's own status is idle.
function _hasRunningTeamActivity(teamActivityByRun) {
  if (!teamActivityByRun) return false;
  return Object.values(teamActivityByRun).some((n) => n?.status === "running");
}

let _optimisticIdCounter = 0;
let _latestFetchTarget = null;
let _submittedRecoveryTimer = null;
let _cancelSafetyTimer = null;
let _sessionListRefetchTimer = null;
let _sessionListRefetchAgentId = null;

function _schedulePendingSessionListRefetch(agentId, get) {
  const normalizedAgentId = String(agentId || "").trim();
  if (!normalizedAgentId) return;
  _sessionListRefetchAgentId = normalizedAgentId;
  if (_sessionListRefetchTimer) {
    clearTimeout(_sessionListRefetchTimer);
  }
  _sessionListRefetchTimer = setTimeout(() => {
    _sessionListRefetchTimer = null;
    const pendingAgentId = _sessionListRefetchAgentId;
    _sessionListRefetchAgentId = null;
    if (!pendingAgentId) return;
    const sessions = get().sessions;
    if (!sessions.some(isSessionRowMetadataPending)) return;
    get().fetchSessions(pendingAgentId, { silent: true });
  }, 750);
}

// Per-session timestamp (ms) of the most recent SSE session_complete event.
// Used to reject silent REST fetches that race backend persistence and would
// overwrite freshly streamed messages with stale, partial data.
const _recentSessionCompleteAtMs = new Map();
const POST_COMPLETE_REST_GUARD_MS = 5000;

// Tracks tool.invoked / tool.completed / tool.failed SSE events per session so
// we can detect tools that completed at the session level but never updated
// message parts (the "stuck spinner" bug).
const _toolLifecycleBySession = new Map();

function _recordToolLifecycleEvent(chatSessionId, eventType, payload) {
  const toolCallId = payload?.toolCallId || payload?.callId || null;
  const toolName = payload?.toolName || payload?.name || "?";
  if (!toolCallId) {
    _crit(
      "AgentStore",
      "warn",
      `tool lifecycle ${eventType} missing toolCallId session=${chatSessionId}`,
      payload
    );
    return;
  }

  let sessionMap = _toolLifecycleBySession.get(chatSessionId);
  if (!sessionMap) {
    sessionMap = new Map();
    _toolLifecycleBySession.set(chatSessionId, sessionMap);
  }

  const prev = sessionMap.get(toolCallId) || {
    toolName,
    invoked: false,
    completed: false,
    failed: false,
  };
  if (eventType === "tool.invoked") prev.invoked = true;
  if (eventType === "tool.completed") prev.completed = true;
  if (eventType === "tool.failed") prev.failed = true;
  prev.toolName = toolName;
  prev.lastEvent = eventType;
  prev.lastAt = Date.now();
  sessionMap.set(toolCallId, prev);

  _crit(
    "AgentStore",
    "info",
    `tool lifecycle ${eventType} session=${chatSessionId} tool=${toolName} callId=${toolCallId} invoked=${prev.invoked} completed=${prev.completed} failed=${prev.failed}`
  );
}

function _auditUnresolvedStreamState(getStore, chatSessionId, context) {
  const detail = getStore().sessionDetail;
  if (!detail || detail.chatSessionId !== chatSessionId) return;

  const chatStatus = getStore().chatStatus;
  const sessionStatus = detail.status || "?";
  const unresolvedParts = findUnresolvedStreamParts(detail.messages);
  const incompleteToolParts = findIncompleteToolParts(detail.messages);
  const sessionMap = _toolLifecycleBySession.get(chatSessionId);
  const invokedNotCompleted = [];

  if (sessionMap) {
    for (const [callId, record] of sessionMap) {
      if (record.invoked && !record.completed && !record.failed) {
        invokedNotCompleted.push({
          toolCallId: callId,
          toolName: record.toolName,
          lastEvent: record.lastEvent,
        });
      }
    }
  }

  const streamingMsgs = (detail.messages || []).filter(
    (m) => m.status === "streaming"
  );
  const sessionTerminal = _isTerminalForStreaming(sessionStatus);
  const chatIdle = chatStatus === "ready";
  const hasUnresolved =
    unresolvedParts.length > 0
    || invokedNotCompleted.length > 0
    || (chatIdle && streamingMsgs.length > 0)
    || (sessionTerminal && chatIdle && unresolvedParts.length > 0);

  if (!hasUnresolved) return;

  const lifecycleSummary = sessionMap
    ? [...sessionMap.entries()].map(
        ([callId, record]) =>
          `${record.toolName}(${callId}):inv=${record.invoked ? 1 : 0}:done=${record.completed ? 1 : 0}:fail=${record.failed ? 1 : 0}`
      )
    : [];
  const streamingSummary = streamingMsgs.length > 0
    ? streamingMsgs
        .map((m) => `${m.messageId || m.id || "?"}{${summarizeParts(m.parts)}}`)
        .join("; ")
    : "none";

  _crit(
    "AgentStore",
    "warn",
    `UNRESOLVED STREAM STATE [${context}] session=${chatSessionId} chatStatus=${chatStatus} sessionStatus=${sessionStatus} unresolvedParts=${unresolvedParts.length} incompleteToolParts=${incompleteToolParts.length} invokedNotCompleted=${invokedNotCompleted.length} streamingMsgs=${streamingMsgs.length} unresolved=[${summarizeUnresolvedStreamParts(detail.messages)}] incompleteTools=[${summarizeIncompleteTools(detail.messages)}] streaming=[${streamingSummary}]`,
    {
      unresolvedParts,
      incompleteToolParts,
      invokedNotCompleted,
      streamingMessageIds: streamingMsgs.map((m) => m.messageId || m.id),
      streamingMessages: streamingMsgs.map((m) => ({
        messageId: m.messageId || m.id || "?",
        parts: summarizeParts(m.parts),
      })),
      toolLifecycle: lifecycleSummary,
      messages: summarizeMessages(detail.messages),
    }
  );
}

function _clearToolLifecycle(chatSessionId) {
  _toolLifecycleBySession.delete(chatSessionId);
}

function _markRecentSessionComplete(chatSessionId) {
  const completedAt = Date.now();
  _recentSessionCompleteAtMs.set(chatSessionId, completedAt);
  setTimeout(() => {
    if (_recentSessionCompleteAtMs.get(chatSessionId) === completedAt) {
      _recentSessionCompleteAtMs.delete(chatSessionId);
    }
  }, POST_COMPLETE_REST_GUARD_MS);
}

let _chatStatusReason = null;
function _markChatStatusReason(reason) {
  _chatStatusReason = reason;
}

function _clearSubmittedRecovery() {
  if (_submittedRecoveryTimer) {
    clearTimeout(_submittedRecoveryTimer);
    _submittedRecoveryTimer = null;
  }
}

function _scheduleSubmittedRecovery(chatSessionId, getStore) {
  _clearSubmittedRecovery();
  _submittedRecoveryTimer = setTimeout(() => {
    _submittedRecoveryTimer = null;
    const cs = getStore().chatStatus;
    if (cs === 'submitted') {
      const streamState = useChatStreamStore.getState().sessionStreamStateBySessionId[chatSessionId];
      _crit(
        "AgentStore",
        "error",
        `STUCK on 'submitted' for 8s — no stream activity. session=${chatSessionId} streamState=${streamState || 'idle'}. Falling back to REST fetch.`
      );
      getStore().fetchSessionDetail(chatSessionId, { silent: true });
    }
  }, 8000);
}

function _clearCancelSafety() {
  if (_cancelSafetyTimer) {
    clearTimeout(_cancelSafetyTimer);
    _cancelSafetyTimer = null;
  }
}

// After a cancel, the UI stays in 'cancelling' (composer disabled) until the authoritative
// session_complete SSE event arrives. This safety net forces 'ready' if that event never lands
// (e.g. SSE dropped), so the composer can never get permanently stuck.
function _scheduleCancelSafety(chatSessionId, getStore, setStore) {
  _clearCancelSafety();
  _cancelSafetyTimer = setTimeout(() => {
    _cancelSafetyTimer = null;
    if (getStore().chatStatus === 'cancelling') {
      _crit(
        "AgentStore",
        "warn",
        `No session_complete after cancel within 10s for session=${chatSessionId}; forcing ready.`
      );
      streamController.clearCursor(chatSessionId);
      _markChatStatusReason('cancelSafetyTimeout');
      setStore({ chatStatus: 'ready' });
    }
  }, 10000);
}

function _isEmptyAssistantStreamMessage(msg) {
  if (!msg || msg.role === "user" || msg.role === "system") return false;
  const content = typeof msg.content === "string" ? msg.content.trim() : "";
  const hasParts = Array.isArray(msg.parts) && msg.parts.length > 0;
  const hasStreamingReasoning =
    Array.isArray(msg.parts) &&
    msg.parts.some((part) => part.type === "reasoning" && part.state === "streaming");
  const hasToolCalls = Array.isArray(msg.toolCalls) && msg.toolCalls.length > 0;
  return !content && !hasParts && !hasStreamingReasoning && !hasToolCalls;
}

function _generateOptimisticId() {
  return `_optimistic_${Date.now()}_${++_optimisticIdCounter}`;
}
function _getLocalUserDisplayName() {
  const { userDisplayName, userName } = useProjectStore.getState();
  return userDisplayName || userName || undefined;
}

// Force a message's `.id` to equal the app message id (`messageId`), not the
// Mongo `_id` that a persisted copy arrives with. The live stream reducer and
// stream controller find the in-flight turn by `.id`, so keeping `.id` =
// messageId upholds the "live and persisted copies share one id" invariant: a
// server snapshot/replace can't orphan the turn the reducer is appending to.
// Centralizes the normalization that was previously re-asserted in each store
// action that ingests server messages.
function _withAppMessageId(msg) {
  if (!msg) return msg;
  const appId = msg.messageId || msg.id;
  if (!appId) return msg;
  return { ...msg, messageId: appId, id: appId };
}

async function _applyAgentAvatarMutation(
  set,
  { agentId, body, method, headers, missingIdMessage, failureMessage }
) {
  const normalizedAgentId = String(agentId || "").trim();
  if (!normalizedAgentId) throw new Error(missingIdMessage);

  const response = await send_request(
    `project-service/agent/${normalizedAgentId}/avatar`,
    body,
    "",
    method,
    headers
  );

  const updated = response?.data || null;
  if (!updated?.agentId) {
    const message = resolveApiErrorMessage(response, failureMessage);
    set({ error: message });
    throw new Error(message);
  }

  set((state) => ({
    agents: state.agents.map((agent) =>
      agent.agentId === normalizedAgentId ? { ...agent, ...updated } : agent
    ),
  }));
  return updated;
}


const useAgentStore = create((set, get) => ({
  agents: [],
  loading: false,
  error: null,
  sendError: null,
  sessions: [],
  toolCatalog: [],
  sessionsLoading: false,
  sessionsHasMore: true,
  sessionsLoadingMore: false,
  sessionDetail: null,
  sessionDetailLoading: false,
  _startingChatContext: null,
  chatStatus: 'ready',
  _isPublicSession: false,
  _workerStatuses: {},
  /** Map<nodeId, { nodeId, agentId, agentName, childSessionId, label, status,
   *  cursor, messages, persistedMessages, startedAt, finishedAt }> — live per-node
   *  team member activity, rebuilt from the team_child_activity relay. */
  _teamActivityByRun: {},
  /** Map<agentId, boolean> — true if agent has any running or externally wakeable session */
  agentHasActiveSession: {},
  /** Map<agentId, number> — latest session activity timestamp (ms) for sidebar ordering */
  agentLastActivityAt: {},
  /** Map<teamId, number> — latest team session activity timestamp (ms) for sidebar ordering */
  teamLastActivityAt: {},
  /** False until recent-sessions bootstrap completes so sidebar can render final order */
  agentSidebarOrderReady: false,
  clearError: () => { _markChatStatusReason('clearError'); set({ chatStatus: 'ready', sendError: null }); },

  /**
   * Bootstrap agent activity with a single bulk fetch via recent-sessions.
   */
  bootstrapAgentActivity: async () => {
    const clientId = _getClientId();
    set({ agentSidebarOrderReady: false });
    if (!clientId) {
      set({ agentSidebarOrderReady: true });
      return;
    }
    try {
      const response = await send_request(
        "ai-service/ai/agent/recent-sessions",
        "",
        { clientId, limit: 50 },
        "GET"
      );
      if (response instanceof Error || !response?.data) {
        set({ agentSidebarOrderReady: true });
        return;
      }
      const sessions = Array.isArray(response.data) ? response.data : [];
      const { activeMap, lastActivityMap, teamLastActivityMap } =
        _buildAgentActivityFromSessions(sessions);
      set({
        agentHasActiveSession: activeMap,
        agentLastActivityAt: lastActivityMap,
        teamLastActivityAt: teamLastActivityMap,
        agentSidebarOrderReady: true,
      });
    } catch (_error) {
      set({ agentSidebarOrderReady: true });
    }
  },

  /**
   * Handle an SSE activity event from the agent channel.
   * Updates agentHasActiveSession and agentLastActivityAt based on session lifecycle events.
   */
  handleAgentActivityEvent: (agentId, eventType, payload) => {
    if (eventType === "new_session") {
      console.debug("[AgentActivity] new_session →", agentId, "active=true");
      set((s) => ({
        agentHasActiveSession: { ...s.agentHasActiveSession, [agentId]: true },
        ...(_bumpAgentLastActivityPatch(
          s,
          agentId,
          _activityMsFromPayload(payload)
        ) || {}),
      }));
      return;
    }

    // session_activity carries wrapped payload from SSE:
    // { streamVersion, type, sessionId, payload: { originalEventType, status, ... } }
    const inner = payload?.payload ?? payload;
    const originalType = inner?.originalEventType;
    const newStatus = inner?.status;
    console.debug("[AgentActivity] event →", agentId, { eventType, originalType, newStatus, inner });

    if (originalType === "status_changed" && typeof newStatus === "string") {
      const active = _isStreamableSessionStatus(newStatus);
      console.debug("[AgentActivity] status_changed →", agentId, "active=", active);
      set((s) => ({
        agentHasActiveSession: {
          ...s.agentHasActiveSession,
          [agentId]: active,
        },
        ...(_bumpAgentLastActivityPatch(
          s,
          agentId,
          _activityMsFromPayload(payload)
        ) || {}),
      }));
      return;
    }

    // session_complete — mark inactive. If another session on this agent is still
    // running, its own status_changed/new_session events will re-activate.
    if (originalType === "session_complete") {
      console.debug("[AgentActivity] session_complete →", agentId, "active=false");
      set((s) => ({
        agentHasActiveSession: { ...s.agentHasActiveSession, [agentId]: false },
        ...(_bumpAgentLastActivityPatch(
          s,
          agentId,
          _activityMsFromPayload(payload)
        ) || {}),
      }));
    }
  },

  bumpAgentLastActivity: (agentId, timestampMs = Date.now()) => {
    if (!agentId) return;
    set((s) => _bumpAgentLastActivityPatch(s, agentId, timestampMs) || s);
  },

  bumpTeamLastActivity: (teamId, timestampMs = Date.now()) => {
    if (!teamId) return;
    set((s) => _bumpTeamLastActivityPatch(s, teamId, timestampMs) || s);
  },

  fetchAgents: async () => {
    set({ loading: true, error: null, agentSidebarOrderReady: false });
    try {
      const response = await send_request(
        "project-service/agent/list",
        "",
        {},
        "GET"
      );
      const nextAgents = response?.data || [];
      const hasEligibleSidebarAgents = nextAgents.some(
        (agent) => agent?.systemAgent !== true
      );
      set({
        agents: nextAgents,
        loading: false,
        agentSidebarOrderReady: !hasEligibleSidebarAgents,
      });
    } catch (e) {
      set({
        error: e.message || "Failed to load agents",
        loading: false,
        agentSidebarOrderReady: true,
      });
    }
  },

  /** Silently refresh agents list without triggering loading skeleton. */
  refreshAgents: async () => {
    try {
      const response = await send_request(
        "project-service/agent/list",
        "",
        {},
        "GET"
      );
      set({ agents: response?.data || [] });
    } catch (_e) {
      // silent — keeps existing list on failure
    }
  },

  createAgent: async (agentData) => {
    try {
      const response = await send_request(
        "project-service/agent/create",
        agentData,
        {},
        "POST"
      );
      if (response?.data) {
        set((s) => ({ agents: [...s.agents, response.data] }));
        return response.data;
      }
      const serverMessage = response?.response?.data?.message;
      const errorId = response?.response?.data?.errorId;
      const status = response?.response?.status;
      const errorMsg = serverMessage
        ? `${serverMessage}${errorId ? ` (Error ID: ${errorId})` : ""}`
        : status
          ? `Server returned ${status}. Please try again.`
          : response?.message || "Failed to create agent";
      set({ error: errorMsg });
      return null;
    } catch (e) {
      const serverMessage = e?.response?.data?.message;
      const errorId = e?.response?.data?.errorId;
      const errorMsg = serverMessage
        ? `${serverMessage}${errorId ? ` (Error ID: ${errorId})` : ""}`
        : e.message || "Failed to create agent";
      set({ error: errorMsg });
      return null;
    }
  },

  updateAgent: async (agentId, agentData) => {
    try {
      const response = await send_request(
        `project-service/agent/${agentId}`,
        agentData,
        {},
        "PUT"
      );
      if (response?.data) {
        set((s) => {
          const agents = [...s.agents];
          const index = agents.findIndex((a) => a.agentId === agentId);
          if (index >= 0) agents[index] = response.data;
          return { agents };
        });
        return response.data;
      }
      // send_request returns the error instead of throwing — extract server message
      const serverMessage = response?.response?.data?.message;
      const errorId = response?.response?.data?.errorId;
      const status = response?.response?.status;
      const errorMsg = serverMessage
        ? `${serverMessage}${errorId ? ` (Error ID: ${errorId})` : ""}`
        : status
          ? `Server returned ${status}. Please try again.`
          : response?.message || "Failed to update agent";
      set({ error: errorMsg });
      return null;
    } catch (e) {
      const serverMessage = e?.response?.data?.message;
      const errorId = e?.response?.data?.errorId;
      const errorMsg = serverMessage
        ? `${serverMessage}${errorId ? ` (Error ID: ${errorId})` : ""}`
        : e.message || "Failed to update agent";
      set({ error: errorMsg });
      return null;
    }
  },

  fetchAgent: async (agentId) => {
    try {
      const response = await send_request(
        `project-service/agent/${agentId}`,
        "",
        {},
        "GET"
      );
      return response?.data || null;
    } catch (e) {
      set({ error: e.message || "Failed to load agent" });
      return null;
    }
  },

  fetchSessions: async (agentId, { silent = false, limit = 25 } = {}) => {
    if (!silent) set({ sessionsLoading: true });
    try {
      const query = limit > 0 ? { limit, offset: 0 } : {};
      const response = await send_request(
        `project-service/agent/${agentId}/sessions`,
        "",
        query,
        "GET"
      );
      const data = response?.data || [];
      const serverSessions = Array.isArray(data) ? data : [];
      const { sessions: priorSessions, _startingChatContext, sessionDetail } =
        get();
      const sessions = reconcileFetchedSessionList({
        priorSessions,
        serverSessions,
        agentId,
        startingChatContext: _startingChatContext,
        sessionDetail,
        localUserDisplayName: _getLocalUserDisplayName(),
      });
      set({
        sessions,
        sessionsHasMore: limit > 0 && serverSessions.length >= limit,
      });
      if (sessions.some(isSessionRowMetadataPending)) {
        _schedulePendingSessionListRefetch(agentId, get);
      }
    } catch (e) {
      set({
        error: e.message || "Failed to load sessions",
        sessions: [],
        sessionsHasMore: false,
      });
    }
    if (!silent) set({ sessionsLoading: false });
  },

  fetchMoreSessions: async (agentId, { limit = 25 } = {}) => {
    const { sessionsLoadingMore, sessionsHasMore, sessions } = get();
    if (sessionsLoadingMore || !sessionsHasMore) return;
    set({ sessionsLoadingMore: true });
    try {
      const offset = sessions.length;
      const response = await send_request(
        `project-service/agent/${agentId}/sessions`,
        "",
        { limit, offset },
        "GET"
      );
      const data = response?.data || [];
      const moreSessions = Array.isArray(data) ? data : [];
      const existingIds = new Set(sessions.map((s) => s.chatSessionId));
      const dedupedMore = moreSessions.filter((s) => !existingIds.has(s.chatSessionId));
      const normalizedMore = normalizeSessionListSenders(
        dedupedMore,
        _getLocalUserDisplayName()
      );
      set({
        sessions: [...sessions, ...normalizedMore],
        sessionsHasMore: moreSessions.length >= limit,
        sessionsLoadingMore: false,
      });
    } catch (_e) {
      set({ sessionsLoadingMore: false });
    }
  },

  fetchToolCatalog: async () => {
    try {
      const clientId = _getClientId();
      const response = await send_request(
        "project-service/agent/tools/catalog",
        "",
        clientId ? { clientId } : {},
        "GET"
      );
      const catalog = response?.data;
      const toolCatalog = Array.isArray(catalog) ? catalog : [];
      set({ toolCatalog });
      return toolCatalog;
    } catch (e) {
      set({
        error: e.message || "Failed to load tool catalog",
        toolCatalog: [],
      });
      return [];
    }
  },

  fetchWorkflowOptions: async () => {
    try {
      const response = await send_request(
        "project-service/agent/workflow-options",
        "",
        {},
        "GET"
      );
      const options = response?.data;
      return Array.isArray(options) ? options : [];
    } catch (e) {
      set({ error: e.message || "Failed to load workflow options" });
      return [];
    }
  },

  // Fallback data loader — only used when SSE is unavailable (poll fallback,
  // submitted-recovery timeout). SSE snapshot is the primary data source.
  fetchSessionDetail: async (chatSessionId, { silent = false } = {}) => {
    const streamState = useChatStreamStore.getState().sessionStreamStateBySessionId[chatSessionId];
    const chatStatus = get().chatStatus;
    if (
      silent
      && streamState === 'connected'
      && (chatStatus === 'submitted' || chatStatus === 'streaming')
    ) {
      return;
    }

    // Reject silent REST fetches that fire shortly after an SSE session_complete.
    // Backend persistence races the SSE event by hundreds of milliseconds, and
    // the REST response routinely returns the just-completed assistant message
    // with only its EARLIEST parts (e.g. only the first tool call) - when
    // _replaceMessages then runs it overwrites the live, fully streamed message
    // and the UI snaps back mid-conversation (the "stuck on get_table" bug).
    // SSE has already delivered the authoritative final state; nothing useful
    // can come from a REST fetch in this window.
    const lastCompleteMs = _recentSessionCompleteAtMs.get(chatSessionId);
    if (silent && lastCompleteMs && (Date.now() - lastCompleteMs) < POST_COMPLETE_REST_GUARD_MS) {
      return;
    }

    _crit(
      "AgentStore",
      "info",
      `fetchSessionDetail session=${chatSessionId} silent=${silent} currentChatStatus=${chatStatus}`
    );
    if (!silent) set({ sessionDetailLoading: true });
    _latestFetchTarget = chatSessionId;
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}`,
        "",
        {},
        "GET"
      );
      const detail = response?.data || null;
      if (detail && !detail.chatSessionId && !detail.messages) {
        if (!silent) set({ sessionDetailLoading: false });
        return;
      }

      if (_latestFetchTarget !== chatSessionId) return;

      // Backend race: the SSE session_complete event is emitted before the DB
      // status flips to a terminal value, so a fetch right after completion
      // can return stale IN_PROGRESS. If our local view is already terminal,
      // treat the response as stale and preserve local status — otherwise we
      // overwrite sessionDetail.status and regress chatStatus ready→streaming
      // with no SSE to recover, leaving the UI stuck forever.
      const localStatus = get().sessionDetail?.chatSessionId === chatSessionId
        ? get().sessionDetail?.status
        : null;
      const localTerminal = _isTerminalForStreaming(localStatus);
      const effectiveDetail = (localTerminal && detail?.status === STATUS_IN_PROGRESS)
        ? { ...detail, status: localStatus }
        : detail;

      get()._replaceMessages(chatSessionId, effectiveDetail?.messages, effectiveDetail);

      if (
        effectiveDetail?.status
        && !_isStreamableSessionStatus(effectiveDetail.status)
      ) {
        _auditUnresolvedStreamState(
          get,
          chatSessionId,
          `fetchSessionDetail:status=${effectiveDetail.status}`
        );
      }

      const streamState = useChatStreamStore.getState().sessionStreamStateBySessionId[chatSessionId];
      const sseActive = streamState === 'connected' || streamState === 'connecting';
      const cs = get().chatStatus;
      const terminalReason = _terminalClearReason(
        effectiveDetail?.status,
        cs,
        `fetchSessionDetail:sseState=${streamState || 'idle'}`
      );
      if (terminalReason) {
        // A terminal backend status is authoritative regardless of SSE state.
        _markChatStatusReason(terminalReason);
        set({ chatStatus: 'ready' });
      } else if (!sseActive) {
        // Non-terminal reconciliation only runs when SSE is NOT connected — when
        // SSE is connected it is the authoritative source for chatStatus.
        if (effectiveDetail?.status === STATUS_IN_PROGRESS) {
          if (cs !== 'streaming') {
            _markChatStatusReason(`fetchSessionDetail(poll):backend=IN_PROGRESS,sseState=${streamState || 'idle'}`);
            set({ chatStatus: 'streaming' });
          }
        } else if (effectiveDetail?.status && effectiveDetail.status !== STATUS_IN_PROGRESS) {
          if (cs === 'submitted' || cs === 'streaming' || cs === 'error') {
            _markChatStatusReason(`fetchSessionDetail(poll):backend=${effectiveDetail.status}`);
            set({ chatStatus: 'ready' });
          }
        }
      }
    } catch (e) {
      set((s) => ({
        error: e.message || "Failed to load session detail",
        sessionDetail: s.sessionDetail || null,
      }));
    }
    // Always clear sessionDetailLoading when this is the latest fetch target,
    // even for silent fetches — SSE-first flow sets sessionDetailLoading: true
    // and relies on the fallback fetch to clear it if SSE fails to connect.
    if (_latestFetchTarget === chatSessionId) {
      set({ sessionDetailLoading: false });
    } else if (!silent) {
      set({ sessionDetailLoading: false });
    }
  },

  disconnectStream: (options) => {
    useChatStreamStore.getState().disconnectStream(options);
  },

  disconnectAgentStream: () => {
    useChatStreamStore.getState().disconnectAgentStream();
  },

  // ── Message primitives ─────────────────────────────────────
  // Every mutation to sessionDetail.messages goes through one of
  // these three functions. Nothing else touches the array.
  // _upsertMessage handles optimistic→server reconciliation via clientRequestId.

  _replaceMessages: (chatSessionId, serverMessages, serverDetail) => {
    const currentId = get().sessionDetail?.chatSessionId;
    const targetSessionId = serverDetail?.chatSessionId ?? chatSessionId ?? null;
    const isSwitchingSession = targetSessionId !== (currentId ?? null);
    if (isSwitchingSession) {
      set({ _workerStatuses: {}, _teamActivityByRun: {} });
    }
    set((s) => {
      // Normalize server messages so `.id` equals the app message id; see
      // `_withAppMessageId` for why this invariant matters.
      const incoming = (serverMessages || []).map(_withAppMessageId);

      // Only preserve local-only messages (optimistic, streaming assistant)
      // when staying on the SAME session. When switching sessions, the old
      // session's messages must not leak into the new one.
      if (!isSwitchingSession) {
        const currentMsgs = s.sessionDetail?.messages || [];
        const incomingIds = new Set();
        for (const m of incoming) {
          if (m.messageId) incomingIds.add(m.messageId);
          if (m.id) incomingIds.add(m.id);
        }

        const localOnly = currentMsgs.filter((m) => {
          if (m._optimistic) return true;
          const mid = m.messageId || m.id;
          if (mid && !incomingIds.has(mid) && m.role === "assistant") return true;
          return false;
        });

        if (localOnly.length > 0) {
          // A live assistant turn not yet in the snapshot is preserved by the
          // id test in `localOnly` above; once the snapshot includes that id it
          // is excluded. The live and persisted copies share one id (the
          // server's iterationMessageId), so no content/parts heuristic is
          // needed to collapse them. Optimistic messages still reconcile once
          // the server echoes them — by clientRequestId, else by role+content
          // for sends without one.
          const nonDup = localOnly.filter((m) => {
            if (m._optimistic && m.clientRequestId && incoming.some((im) => im.clientRequestId === m.clientRequestId)) {
              return false;
            }
            if (m._optimistic && incoming.some((im) => im.role === m.role && im.content === m.content)) {
              return false;
            }
            return true;
          });
          if (nonDup.length > 0) incoming.push(...nonDup);
        }
      }

      const orderedMessages = sortSessionMessages(incoming);

      const detail = serverDetail
        ? { ...serverDetail, messages: orderedMessages }
        : s.sessionDetail
          ? { ...s.sessionDetail, chatSessionId, messages: orderedMessages }
          : { chatSessionId, messages: orderedMessages, status: "IN_PROGRESS" };

      return { sessionDetail: detail };
    });
    const replacedMessages =
      get().sessionDetail?.chatSessionId === targetSessionId
        ? get().sessionDetail?.messages
        : null;
    const unresolvedIncoming = findUnresolvedStreamParts(replacedMessages);
    if (unresolvedIncoming.length > 0) {
      _crit(
        "AgentStore",
        "warn",
        `_replaceMessages unresolved stream parts session=${targetSessionId || chatSessionId || "?"} count=${unresolvedIncoming.length} [${summarizeUnresolvedStreamParts(replacedMessages)}]`,
        { unresolvedParts: unresolvedIncoming }
      );
    }
    if (targetSessionId) {
      get()._syncSessionListFromDetail(targetSessionId);
    }
  },

  _syncSessionListFromDetail: (chatSessionId) => {
    set((s) => {
      const detail = s.sessionDetail;
      if (!detail || detail.chatSessionId !== chatSessionId) return {};
      return {
        sessions: applySessionDetailToList(s.sessions, detail, {
          clearOptimistic: false,
        }),
      };
    });
  },

  _upsertMessage: (chatSessionId, msg) => {
    set((s) => {
      const detail = s.sessionDetail;
      if (!detail || detail.chatSessionId !== chatSessionId) {
        return {};
      }
      if (!msg) return {};

      const id = msg.messageId || msg.id;
      const before = detail.messages || [];

      // Reconciling a live copy with its persisted server copy: replace in place
      // then re-order, because the server copy carries the messageSequence the
      // live copy lacked. Without the re-sort a marker that arrived first would
      // stay stuck ahead of the turn it evaluated.
      if (id) {
        const idx = before.findIndex(
          (m) => (m.messageId && m.messageId === id) || (m.id && m.id === id)
        );
        if (idx !== -1) {
          const msgs = [...before];
          msgs[idx] = msg;
          const sorted = sortSessionMessages(msgs);
          return { sessionDetail: { ...detail, messages: sorted } };
        }
      }

      if (msg.clientRequestId || (msg.role === "user" && !msg._optimistic)) {
        const optIdx = msg.clientRequestId
          ? before.findIndex((m) => m._optimistic && m.clientRequestId === msg.clientRequestId)
          : before.findIndex((m) => m._optimistic && m.role === "user" && m.content === msg.content);
        if (optIdx !== -1) {
          const msgs = [...before];
          const prior = before[optIdx];
          msgs[optIdx] = {
            ...msg,
            senderDisplayName:
              msg.senderDisplayName || prior.senderDisplayName || undefined,
            senderName: msg.senderName || prior.senderName || undefined,
          };
          return { sessionDetail: { ...detail, messages: sortSessionMessages(msgs) } };
        }
      }

      // The live streamed copy and the server-persisted copy of an assistant
      // turn share one id (the server's iterationMessageId), so the id-match
      // path above reconciles them. No content/parts dedup is needed.
      const appended = sortSessionMessages([...before, msg]);
      return {
        sessionDetail: {
          ...detail,
          messages: appended,
        },
      };
    });
    get()._syncSessionListFromDetail(chatSessionId);
  },

  _removeMessage: (chatSessionId, messageId) => {
    set((s) => {
      const detail = s.sessionDetail;
      if (!detail || detail.chatSessionId !== chatSessionId) return {};
      const msgs = (detail.messages || []).filter((m) => m.messageId !== messageId);
      return { sessionDetail: { ...detail, messages: msgs } };
    });
  },

  // ── End message primitives ────────────────────────────────

  // Applies one live UI-chunk SSE event. Thin delegate to the stream controller
  // (see conversation/stream/streamController.js), which owns the reducer
  // cursors, the intra-frame flush buffer, and the animation-frame throttle and
  // writes the in-flight assistant turn straight into sessionDetail.messages —
  // the single source of truth. Kept as a store method so the SSE wiring and
  // tests can drive it via the store.
  _applyStreamChunk: (eventName, data, sessionId) => {
    streamController.applyStreamChunk(eventName, data, sessionId);
  },

  // Applies one team_child_activity relay event into the per-run activity slice via the pure
  // reduceTeamActivity reducer (see conversation/stream/teamActivityReducer). The reducer returns
  // the same map reference for no-op events (unknown finish, replay after completion) so we skip
  // the state update.
  _applyTeamActivity: (data) => {
    const payload = getSseEventPayload(data);
    if (!payload) return;
    set((s) => {
      const next = reduceTeamActivity(s._teamActivityByRun, payload);
      return next === s._teamActivityByRun ? {} : { _teamActivityByRun: next };
    });
  },

  // Lazily fetch a team run's full persisted child transcript (for cold load /
  // refresh, when the live relay produced no streamed messages). Fetched once per
  // run; the card prefers persistedMessages over the live stream. `seed`
  // reconstructs a missing entry from the projected message metadata so persisted
  // cards (no live activity) can still hydrate on expand.
  _loadTeamChildTranscript: async (runId, childSessionId, seed = null) => {
    const entry = get()._teamActivityByRun[runId];
    if (entry && (entry._loadingTranscript || entry.persistedMessages)) return;
    const parentSessionId = get().sessionDetail?.chatSessionId;
    if (!parentSessionId || !childSessionId) return;

    const mark = (patch) =>
      set((s) => {
        const e = s._teamActivityByRun[runId] || seed || { runId };
        return {
          _teamActivityByRun: { ...s._teamActivityByRun, [runId]: { ...e, ...patch } },
        };
      });

    mark({ _loadingTranscript: true });
    const res = await getTeamChildSession(parentSessionId, childSessionId);
    const messages = Array.isArray(res?.data?.messages) ? res.data.messages : [];
    mark({ persistedMessages: messages, _loadingTranscript: false });
  },

  subscribeToAgent: async (agentId) => {
    await useChatStreamStore.getState().subscribeToAgent(agentId, (aid) => {
      get().fetchSessions(aid, { silent: true });
    });
  },

  /**
   * Subscribe to session SSE events.
   * For private sessions: subscribeToSession(chatSessionId)
   * For public sessions:  subscribeToSession(chatSessionId, { publicSessionToken: "pct-..." })
   */
  subscribeToSession: async (chatSessionId, options = {}) => {
    const isPublic = !!options?.publicSessionToken;
    const connectId = isPublic ? options.publicSessionToken : chatSessionId;
    set({ _isPublicSession: isPublic });
    // Start each (re)subscribe with a fresh stream cursor so replayed chunks
    // rebuild the in-flight turn from scratch rather than continuing stale
    // pointers from a previous connection. Clearing all cursors (not just this
    // session's) also reclaims any left over from a session the user navigated
    // away from mid-stream, since only one session streams at a time.
    streamController.clearAllCursors();

    const result = await useChatStreamStore.getState().subscribeToSession(
      connectId,
      chatSessionId,
      {
        reconnectWhileSubscribed: options?.reconnectWhileSubscribed === true,
        forceReconnect: options?.forceReconnect === true,
        skipAuth: isPublic,
        onEvent: (eventName, data, sessionId) => {
          const eventType = getSseEventType(eventName, data);
          if (eventType === "team_child_activity") {
            get()._applyTeamActivity(data);
            return;
          }
          if (eventType === "ui_chunk") {
            get()._applyStreamChunk(eventName, data, sessionId);
            return;
          }
          get()._handleSseEvent(eventName, data, sessionId);
        },
        shouldReconnect: () => {
          const status = get().sessionDetail?.status;
          const hasWorkers = _hasIncompleteWorkers(get()._workerStatuses);
          const hasTeam = _hasRunningTeamActivity(get()._teamActivityByRun);
          return _isStreamableSessionStatus(status) || hasWorkers || hasTeam;
        },
        onPollFallback: (sid) => {
          get().fetchSessionDetail(sid, { silent: true });
        },
        pollIntervalMs: 3000,
        isSessionActive: () => {
          const s = get().sessionDetail;
          const cs = get().chatStatus;
          const hasWorkers = _hasIncompleteWorkers(get()._workerStatuses);
          const hasTeam = _hasRunningTeamActivity(get()._teamActivityByRun);
          return (
            _isStreamableSessionStatus(s?.status) || cs !== 'ready' || hasWorkers || hasTeam
          );
        },
        isSessionTerminal: () => {
          const d = get().sessionDetail;
          const cs = get().chatStatus;
          const hasWorkers = _hasIncompleteWorkers(get()._workerStatuses);
          const hasTeam = _hasRunningTeamActivity(get()._teamActivityByRun);
          if (!d || d.chatSessionId !== chatSessionId) return false;
          if (_isStreamableSessionStatus(d.status)) return false;
          if (cs !== 'ready') return false;
          return !hasWorkers && !hasTeam;
        },
      }
    );
    if (result?.aborted) {
      return;
    }
    const currentStatus = get().chatStatus;
    if (currentStatus === 'streaming' || currentStatus === 'error') {
      _markChatStatusReason(`streamClosed:connectionFailed=${!!result?.connectionFailed}`);
      set({ chatStatus: 'ready' });
      _auditUnresolvedStreamState(
        get,
        chatSessionId,
        `streamClosed:connectionFailed=${!!result?.connectionFailed}`
      );
    }
    if (result?.connectionFailed) {
      get().fetchSessionDetail(chatSessionId, { silent: true });
    }
  },

  /**
   * Reopen the chat stream for an already-open session that was reactivated
   * out-of-band. When an artifact requested mid-conversation is submitted on a
   * separate page, the backend flips the (COMPLETED) session back to a
   * streamable status. That session's chat stream closed when the prior turn
   * completed, and the subscription effect is keyed only on selectedSessionId,
   * so nothing re-subscribes it — forms keep the session streamable so their
   * stream never closes, which is why this gap is artifact-specific. The
   * sessions-list status is the surface-independent signal that the session is
   * live again.
   */
  reopenReactivatedSessionStream: (chatSessionId, status) => {
    if (!chatSessionId || chatSessionId === "__pending__") return;
    if (!_isKnownStreamableSessionStatus(status)) return;
    // Never tear down a healthy live turn: while a stream is open the stream
    // store stays subscribed to this session; it resets to null on clean close.
    if (
      useChatStreamStore.getState().getSubscribedSessionId() === chatSessionId
    ) {
      return;
    }
    get().subscribeToSession(chatSessionId, {
      reconnectWhileSubscribed: true,
      forceReconnect: true,
    });
  },

  _handleSseEvent: (eventName, data, chatSessionId) => {
    const type = getSseEventType(eventName, data);
    const payload = getSseEventPayload(data);
    const streamState = useChatStreamStore.getState().sessionStreamStateBySessionId[chatSessionId];
    _crit(
      "AgentStore",
      "info",
      `_handleSseEvent type=${type} session=${chatSessionId} sseState=${streamState || 'idle'} chatStatus=${get().chatStatus}`
    );
    if (_ACTIVE_RUN_EVENT_TYPES.has(type)) {
      _markSubmittedStreamStarted(`sseActivity:${type}`, get, set);
    }
    if (
      type === "tool.invoked"
      || type === "tool.completed"
      || type === "tool.failed"
    ) {
      _recordToolLifecycleEvent(chatSessionId, type, payload);
    }
    if (isTaskQueueSseEvent(type)) {
      const queueEvent = extractTaskQueueEvent(eventName, data);
      if (queueEvent && chatSessionId) {
        useTaskQueueStore.getState().setTaskQueueEvent(chatSessionId, queueEvent);
      }
    }

    if (type === "snapshot") {
      if (!payload) {
        set({ sessionDetailLoading: false });
        return;
      }
      const currentDetail = get().sessionDetail;
      const currentChatStatus = get().chatStatus;
      const isSameSession = currentDetail?.chatSessionId === chatSessionId;
      const terminalReason = isSameSession
        ? _terminalClearReason(payload.status, currentChatStatus, "sseSnapshot")
        : null;
      if (terminalReason) {
        // A terminal snapshot mid-stream means the session finished while we were
        // briefly disconnected. We still skip _replaceMessages here to avoid
        // clobbering the live-streamed messages, but we MUST reconcile the busy
        // state: session_complete is unrecoverable once the backend purges its
        // replay buffer, so this snapshot is the only signal left to clear
        // chatStatus — otherwise the UI stays on "Thinking" forever.
        _clearSubmittedRecovery();
        _markChatStatusReason(terminalReason);
        set((s) => ({
          sessionDetailLoading: false,
          chatStatus: 'ready',
          sessionDetail:
            s.sessionDetail && s.sessionDetail.chatSessionId === chatSessionId
              ? { ...s.sessionDetail, status: payload.status }
              : s.sessionDetail,
        }));
        return;
      }
      get()._replaceMessages(chatSessionId, payload.messages, payload);

      // SSE snapshot is the authoritative initial state.
      if (_isActivelyRunningSessionStatus(payload.status)) {
        _clearSubmittedRecovery();
        _markChatStatusReason(`sseSnapshot:backend=${payload.status}`);
        set({ sessionDetailLoading: false, chatStatus: 'streaming' });
      } else if (_isKnownStreamableSessionStatus(payload.status)) {
        const cs = get().chatStatus;
        _clearSubmittedRecovery();
        if (cs === 'submitted' || cs === 'streaming' || cs === 'error') {
          _markChatStatusReason(`sseSnapshot:backend=${payload.status}`);
          set({ sessionDetailLoading: false, chatStatus: 'ready' });
        } else {
          set({ sessionDetailLoading: false });
        }
      } else if (payload.status && payload.status !== STATUS_IN_PROGRESS) {
        const cs = get().chatStatus;
        if (cs === 'streaming' || cs === 'error') {
          _markChatStatusReason(`sseSnapshot:backend=${payload.status}`);
          set({ sessionDetailLoading: false, chatStatus: 'ready' });
        } else {
          set({ sessionDetailLoading: false });
        }
      } else {
        set({ sessionDetailLoading: false });
      }
    } else if (type === "message_added") {
      const msg = extractMessageAddedChatMessage(eventName, data);
      if (!msg) {
        return;
      }
      if (msg.role === "assistant") {
        let compId = null;
        const parts = msg.parts || [];
        for (const part of parts) {
          if (part.type === "tool" && part.input) {
            const inp = typeof part.input === "string" ? (() => { try { return JSON.parse(part.input); } catch (_e) { return null; } })() : part.input;
            if (inp?.componentId) { compId = inp.componentId; break; }
          }
        }
        if (!compId && msg.toolCall) {
          const toolArgs = msg.toolCall.arguments;
          if (typeof toolArgs === "string") {
            try { compId = JSON.parse(toolArgs)?.componentId; } catch (_e) { /* ignore */ }
          } else if (toolArgs && typeof toolArgs === "object") {
            compId = toolArgs.componentId;
          }
        }
        if (compId) {
          const handler = get()._canvasUpdateHandler;
          if (handler) handler({ action: '_tool_activity_start', componentId: compId });
        }
      }

      // Normalize `.id` to the app message id; see `_withAppMessageId`. Keeps
      // the live stream reducer and controller finding this turn across
      // step-start iterations instead of minting a duplicate (flicker).
      const normalized = _withAppMessageId(msg);

      if (get().chatStatus === 'submitted') {
        _markChatStatusReason('sseMessageAdded:firstMessage');
        set({ chatStatus: 'streaming' });
      }

      const currentMsgs = get().sessionDetail?.messages || [];
      const id = normalized.messageId;

      if (msg.role === "user") {
        if (id) {
          const idExists = currentMsgs.some(
            (m) => (m.messageId && m.messageId === id) || (m.id && m.id === id)
          );
          if (idExists) {
            return;
          }
        }
        const contentExists = currentMsgs.some(
          (m) => !m._optimistic && m.role === "user" && m.content === msg.content
        );
        if (contentExists) {
          return;
        }
      }

      if (_isEmptyAssistantStreamMessage(normalized)) {
        return;
      }

      get()._upsertMessage(chatSessionId, normalized);

      if (msg.role === "assistant") {
        const parts = normalized.parts || [];
        const hasDiagnosticParts = parts.some(
          (p) => p.type === "tool" || p.type === "reasoning" || p.state === "streaming"
        );
        if (hasDiagnosticParts) {
          const unresolvedParts = findUnresolvedStreamParts([normalized]);
          _crit(
            "AgentStore",
            "info",
            `message_added assistant parts session=${chatSessionId} msgId=${normalized.messageId || normalized.id} msgStatus=${normalized.status || "?"} unresolvedParts=${unresolvedParts.length} parts={${summarizeParts(parts)}}`
          );
          if (unresolvedParts.length > 0) {
            _auditUnresolvedStreamState(get, chatSessionId, "message_added");
          }
        }
      }
    } else if (type === "status_changed") {
      const nextStatus = typeof payload === "string" ? payload : payload?.status;
      if (_isActivelyRunningSessionStatus(nextStatus)) {
        _clearSubmittedRecovery();
        _markChatStatusReason(`sseStatusChanged:${nextStatus}`);
        set({ chatStatus: 'streaming' });
      } else if (_isKnownStreamableSessionStatus(nextStatus)) {
        const cs = get().chatStatus;
        _clearSubmittedRecovery();
        if (cs === 'streaming' || cs === 'submitted' || cs === 'error') {
          _markChatStatusReason(`sseStatusChanged:${nextStatus}`);
          set({ chatStatus: 'ready' });
        }
      } else if (nextStatus && nextStatus !== STATUS_IN_PROGRESS) {
        const cs = get().chatStatus;
        if (cs === 'streaming' || cs === 'submitted' || cs === 'error') {
          _markChatStatusReason(`sseStatusChanged:${nextStatus}`);
          set({ chatStatus: 'ready' });
        }
      }
      set((s) => {
        if (
          s.sessionDetail &&
          s.sessionDetail.chatSessionId === chatSessionId
        ) {
          return {
            sessionDetail: {
              ...s.sessionDetail,
              status: nextStatus,
              statusReason:
                nextStatus === "ERROR"
                  ? s.sessionDetail.statusReason || null
                  : null,
            },
          };
        }
        return {};
      });
      if (get()._isPublicSession && nextStatus !== "IN_PROGRESS") {
        const cb = get()._publicSessionChangeCallback;
        if (cb) cb();
      }
      if (nextStatus && !_isStreamableSessionStatus(nextStatus)) {
        _auditUnresolvedStreamState(get, chatSessionId, `status_changed:${nextStatus}`);
      }
    } else if (type === "session_complete") {
      _markRecentSessionComplete(chatSessionId);
      _clearSubmittedRecovery();
      _clearCancelSafety();
      // Flush any not-yet-flushed live turn so the final assistant turn is in
      // sessionDetail.messages before finalizing (a cancel can land between the
      // last chunk and its scheduled animation frame).
      streamController.flushPendingForSession(chatSessionId);

      // On a cancel, the partial response is the final in-flight assistant
      // message that has visible answer text. Flag only that one interrupted so
      // the UI shows the "stopped" marker immediately. This mirrors the backend,
      // which persists exactly that message with interrupted=true (earlier
      // tool/reasoning iterations are persisted normally or not at all), so a
      // later reload reconciles to the identical state without spurious markers
      // on prior bubbles. The streamed turn already lives in
      // sessionDetail.messages (the single source of truth), so it is marked in
      // place — there is no separate builder copy to re-upsert.
      if ((payload?.status || "") === "CANCELLED") {
        const msgs = get().sessionDetail?.messages || [];
        for (let i = msgs.length - 1; i >= 0; i--) {
          const m = msgs[i];
          if (
            m &&
            m.role === "assistant" &&
            Array.isArray(m.parts) &&
            m.parts.some(
              (p) => p?.type === "text" && typeof p.text === "string" && p.text.trim()
            )
          ) {
            get()._upsertMessage(chatSessionId, { ...m, interrupted: true });
            break;
          }
        }
      }

      streamController.clearCursor(chatSessionId);
      _markChatStatusReason('sseSessionComplete');
      set((s) => {
        const updates = {
          chatStatus: 'ready',
        };
        if (
          s.sessionDetail &&
          s.sessionDetail.chatSessionId === chatSessionId
        ) {
          const completion = payload || {};
          const newStatus = completion.status || "COMPLETED";
          const newIterations =
            completion.iterationsUsed ?? s.sessionDetail.iterationsUsed;
          const completionReason = normalizeSessionStatusReason(
            completion.reason || completion.error
          );
          const currentReason = normalizeSessionStatusReason(
            s.sessionDetail.statusReason
          );
          const nextStatusReason =
            completionReason || (newStatus === "ERROR" ? currentReason : null);
          updates.sessionDetail = {
            ...s.sessionDetail,
            status: newStatus,
            iterationsUsed: newIterations,
            statusReason: nextStatusReason,
          };
        }
        return updates;
      });
      _auditUnresolvedStreamState(get, chatSessionId, "session_complete");
      _clearToolLifecycle(chatSessionId);
      if (!get()._isPublicSession) {
        get().refreshSessionInList(chatSessionId);
        // Intentionally no fetchSessionDetail here. session_complete is the
        // authoritative end-of-stream signal — sessionDetail.status,
        // iterationsUsed, statusReason were just written from the payload above,
        // and all messages arrived via SSE. A REST fetch here races backend
        // persistence and the response (which is missing the just-streamed
        // messages) overwrites the live SSE state via _replaceMessages.
      } else {
        const cb = get()._publicSessionChangeCallback;
        if (cb) cb();
      }
    } else if (type === "worker_status_changed") {
      if (payload?.workerSessionId) {
        set((s) => ({
          _workerStatuses: {
            ...s._workerStatuses,
            [payload.workerSessionId]: payload,
          },
        }));
      }
    } else if (type === "canvas_update") {
      const handler = get()._canvasUpdateHandler;
      if (handler) {
        handler(payload);
      }
    } else if (type === "tool_activity") {
      if (payload?.componentId) {
        const handler = get()._canvasUpdateHandler;
        if (handler) handler({ action: '_tool_activity_start', componentId: payload.componentId });
      }
    }
  },

  _canvasUpdateHandler: null,
  setCanvasUpdateHandler: (fn) => {
    set({ _canvasUpdateHandler: fn });
  },

  _publicSessionChangeCallback: null,
  setPublicSessionChangeCallback: (fn) => set({ _publicSessionChangeCallback: fn }),

  startChat: async (
    agentId,
    message,
    imageUrls,
    {
      projectId,
      projectName,
      tableId,
      tableName,
      boardId,
      boardName,
      teamId,
      teamName,
      artifactDefinitionId,
      artifactInstanceId,
      artifactComponentId,
      artifactTitle,
    } = {}
  ) => {
    const optimisticId = _generateOptimisticId();
    const clientRequestId = crypto.randomUUID();
    const senderDisplayName = _getLocalUserDisplayName();
    const optimisticMsg = {
      messageId: optimisticId,
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
      imageUrls: imageUrls || undefined,
      senderDisplayName,
      _optimistic: true,
      clientRequestId,
    };
    _clearSubmittedRecovery();
    _markChatStatusReason('startChat');
    set({
      chatStatus: 'submitted',
      sendError: null,
      _startingChatContext: {
        agentId,
        message,
        senderDisplayName: senderDisplayName || "You",
        clientRequestId,
      },
    });
    get()._replaceMessages(null, [optimisticMsg], {
      chatSessionId: null,
      messages: [optimisticMsg],
      status: "IN_PROGRESS",
    });
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const payload = { message, userTimezone, clientRequestId };
      if (imageUrls && imageUrls.length > 0) payload.imageUrls = imageUrls;
      if (projectId) payload.projectId = projectId;
      if (projectName) payload.projectName = projectName;
      if (tableId) payload.tableId = tableId;
      if (tableName) payload.tableName = tableName;
      if (boardId) payload.boardId = boardId;
      if (boardName) payload.boardName = boardName;
      if (teamId) payload.teamId = teamId;
      if (teamName) payload.teamName = teamName;
      if (artifactDefinitionId) payload.artifactDefinitionId = artifactDefinitionId;
      if (artifactInstanceId) payload.artifactInstanceId = artifactInstanceId;
      if (artifactComponentId) payload.artifactComponentId = artifactComponentId;
      if (artifactTitle) payload.artifactTitle = artifactTitle;
      const response = await send_request(
        `project-service/agent/${agentId}/chat`,
        payload,
        {},
        "POST"
      );
      const data = response?.data;
      if (data?.chatSessionId) {
        const optimisticListRow = buildOptimisticSessionListRow({
          chatSessionId: data.chatSessionId,
          message,
          senderDisplayName,
        });
        set((s) => ({
          sessionDetail: {
            ...s.sessionDetail,
            chatSessionId: data.chatSessionId,
            status: "IN_PROGRESS",
          },
          ...(_bumpAgentLastActivityPatch(s, agentId) || {}),
          _startingChatContext: null,
          sessions: upsertSessionListRow(s.sessions, optimisticListRow),
        }));
        get().subscribeToSession(data.chatSessionId, { reconnectWhileSubscribed: true, forceReconnect: true });
        _scheduleSubmittedRecovery(data.chatSessionId, get);
        get().fetchSessions(agentId, { silent: true });
      }
      return data;
    } catch (e) {
      _clearSubmittedRecovery();
      _markChatStatusReason('startChat:error');
      set({
        sendError: e.message || "Failed to start chat",
        chatStatus: 'error',
        sessionDetail: null,
        _startingChatContext: null,
      });
      return null;
    }
  },

  // Render a team conversation optimistically the instant the user sends from the
  // welcome screen, before the parent session exists. Mirrors startChat's optimistic
  // setup so the user's message + "working" indicator appear immediately instead of
  // the welcome screen sitting frozen during session creation. The optimistic user
  // message reconciles (by role+content) once the projected message streams in.
  startTeamChatOptimistic: (message) => {
    const optimisticMsg = {
      messageId: _generateOptimisticId(),
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
      senderDisplayName: _getLocalUserDisplayName(),
      _optimistic: true,
    };
    _markChatStatusReason('startTeamChat');
    set({
      chatStatus: 'submitted',
      sendError: null,
      sessionDetail: { chatSessionId: null, status: "IN_PROGRESS", messages: [optimisticMsg] },
      _teamActivityByRun: {},
    });
  },

  // Adopt the real parent session id onto the optimistic detail in place, so the
  // subsequent requestedSessionId-driven fetch treats it as the same session and
  // preserves the optimistic message instead of dropping it as a session switch.
  adoptTeamSessionId: (chatSessionId) => {
    set((s) =>
      s.sessionDetail
        ? { sessionDetail: { ...s.sessionDetail, chatSessionId } }
        : {}
    );
  },

  sendMessage: async (chatSessionId, agentId, message, imageUrls) => {
    _log("Send", `sendMessage: session=${chatSessionId} chatStatus=${get().chatStatus} subscribedSession=${useChatStreamStore.getState().getSubscribedSessionId()}`);
    const optimisticId = _generateOptimisticId();
    const clientRequestId = crypto.randomUUID();
    const senderDisplayName = _getLocalUserDisplayName();
    _clearSubmittedRecovery();
    _markChatStatusReason('sendMessage');
    set({ chatStatus: 'submitted', sendError: null });
    get()._upsertMessage(chatSessionId, {
      messageId: optimisticId,
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
      imageUrls: imageUrls || undefined,
      senderDisplayName,
      _optimistic: true,
      clientRequestId,
    });
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const payload = { agentId, message, userTimezone, clientRequestId };
      if (imageUrls && imageUrls.length > 0) {
        payload.imageUrls = imageUrls;
      }
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/message`,
        payload,
        {},
        "POST"
      );
      const blockedPayload = extractBlockedIngressPayload(response);
      if (blockedPayload) {
        get().disconnectStream();
        get()._removeMessage(chatSessionId, optimisticId);
        _markChatStatusReason('sendMessage:blockedIngress');
        set((s) => {
          const detail = s.sessionDetail?.chatSessionId === chatSessionId
            ? s.sessionDetail
            : { chatSessionId, messages: [] };
          return {
            chatStatus: 'ready',
            error: null,
            sessionDetail: {
              ...detail,
              status: blockedPayload.sessionStatus,
              blockedWhileReviewMessage: blockedPayload.message,
              statusReason: null,
            },
          };
        });
        get().refreshSessionInList(chatSessionId);
        return { chatSessionId, blocked: true };
      }
      const statusCode = extractApiStatusCode(response);
      if (statusCode >= 400) {
        throw new Error(
          resolveApiErrorMessage(response, "Failed to send message")
        );
      }
      get().subscribeToSession(chatSessionId, { reconnectWhileSubscribed: true });
      _scheduleSubmittedRecovery(chatSessionId, get);
      if (agentId) {
        get().bumpAgentLastActivity(agentId);
      }
      return { chatSessionId };
    } catch (e) {
      _clearSubmittedRecovery();
      get().disconnectStream();
      const errorMsg = resolveApiErrorMessage(e, "Failed to send message");
      get()._removeMessage(chatSessionId, optimisticId);
      _markChatStatusReason('sendMessage:error');
      set({ sendError: errorMsg, chatStatus: 'error' });
      return null;
    }
  },

  sendPublicMessage: async (chatSessionId, publicSessionToken, agentId, message, imageUrls, senderDisplayName) => {
    const optimisticId = _generateOptimisticId();
    const clientRequestId = crypto.randomUUID();
    _clearSubmittedRecovery();
    _markChatStatusReason('sendPublicMessage');
    set({ chatStatus: 'submitted', sendError: null });
    get()._upsertMessage(chatSessionId, {
      messageId: optimisticId,
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
      imageUrls: imageUrls || undefined,
      senderDisplayName,
      _optimistic: true,
      clientRequestId,
    });
    try {
      const payload = { agentId, message, clientRequestId };
      if (imageUrls && imageUrls.length > 0) payload.imageUrls = imageUrls;
      await usePublicChatStore.getState().sendPublicChatMessage(publicSessionToken, payload);
      get().subscribeToSession(chatSessionId, { publicSessionToken, reconnectWhileSubscribed: true, forceReconnect: true });
      _scheduleSubmittedRecovery(chatSessionId, get);
      return { chatSessionId };
    } catch (e) {
      _clearSubmittedRecovery();
      get()._removeMessage(chatSessionId, optimisticId);
      const blockedPayload = extractBlockedIngressPayload(e);
      if (blockedPayload) {
        _markChatStatusReason('sendPublicMessage:blockedIngress');
        set((s) => {
          const detail = s.sessionDetail?.chatSessionId === chatSessionId
            ? s.sessionDetail
            : { chatSessionId, messages: [] };
          return {
            chatStatus: 'ready',
            sessionDetail: {
              ...detail,
              status: blockedPayload.sessionStatus,
              blockedWhileReviewMessage: blockedPayload.message,
              statusReason: null,
            },
          };
        });
        return { chatSessionId, blocked: true };
      }
      _markChatStatusReason('sendPublicMessage:error');
      set({ chatStatus: 'error', sendError: resolveApiErrorMessage(e, "Failed to send message") });
      return null;
    }
  },

  startHumanTakeover: async (chatSessionId) => {
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/human-takeover/start`,
        {},
        {},
        "POST"
      );
      const data = response?.data || null;
      set((s) => {
        if (s.sessionDetail?.chatSessionId === chatSessionId) {
          return {
            sessionDetail: {
              ...s.sessionDetail,
              status: data?.status || "HUMAN_TAKEOVER",
              statusReason: null,
            },
          };
        }
        return {};
      });
      await get().fetchSessionDetail(chatSessionId, { silent: true });
      get().refreshSessionInList(chatSessionId);
      return data;
    } catch (e) {
      const blockedPayload = extractBlockedIngressPayload(e);
      if (blockedPayload) {
        set((s) => {
          const existingDetail =
            s.sessionDetail?.chatSessionId === chatSessionId
              ? s.sessionDetail
              : { chatSessionId, messages: [] };
          return {
            sessionDetail: {
              ...existingDetail,
              status: blockedPayload.sessionStatus,
              blockedWhileReviewMessage: blockedPayload.message,
              statusReason: null,
            },
          };
        });
        get().refreshSessionInList(chatSessionId);
        return { blocked: true, ...blockedPayload };
      }
      set({
        error: resolveApiErrorMessage(e, "Failed to start human takeover"),
      });
      return null;
    }
  },

  endHumanTakeover: async (chatSessionId) => {
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/human-takeover/end`,
        {},
        {},
        "POST"
      );
      const data = response?.data || null;
      set((s) => {
        if (s.sessionDetail?.chatSessionId === chatSessionId) {
          return {
            sessionDetail: {
              ...s.sessionDetail,
              status: data?.status || "COMPLETED",
              statusReason: null,
            },
          };
        }
        return {};
      });
      await get().fetchSessionDetail(chatSessionId, { silent: true });
      get().refreshSessionInList(chatSessionId);
      return data;
    } catch (e) {
      const blockedPayload = extractBlockedIngressPayload(e);
      if (blockedPayload) {
        set((s) => {
          const existingDetail =
            s.sessionDetail?.chatSessionId === chatSessionId
              ? s.sessionDetail
              : { chatSessionId, messages: [] };
          return {
            sessionDetail: {
              ...existingDetail,
              status: blockedPayload.sessionStatus,
              blockedWhileReviewMessage: blockedPayload.message,
              statusReason: null,
            },
          };
        });
        get().refreshSessionInList(chatSessionId);
        return { blocked: true, ...blockedPayload };
      }
      set({
        error: resolveApiErrorMessage(e, "Failed to end human takeover"),
      });
      return null;
    }
  },

  cancelSession: async (chatSessionId) => {
    // Enter a 'cancelling' state and keep the composer locked. Do NOT optimistically flip to
    // 'ready' on the cancel HTTP 200 — the backend may still be unwinding the in-flight run, and
    // re-enabling the composer here lets the user send a new message that races a not-yet-stopped
    // run. The authoritative session_complete SSE event drives the transition to 'ready'.
    _markChatStatusReason('cancelSession');
    set((s) => {
      const updates = { chatStatus: 'cancelling' };
      if (s.sessionDetail?.chatSessionId === chatSessionId) {
        updates.sessionDetail = { ...s.sessionDetail, status: "CANCELLING" };
      }
      return updates;
    });
    _scheduleCancelSafety(chatSessionId, get, set);
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/cancel`,
        {},
        {},
        "POST"
      );
      // send_request resolves (does not throw) for most failures — it returns the error object.
      // Detect a non-success status here and route it through the catch, otherwise a failed cancel
      // would leave the UI stuck in 'cancelling' until the 10s safety timeout with no error shown.
      const statusCode = extractApiStatusCode(response);
      if (statusCode === 0 || statusCode >= 400) {
        throw new Error(resolveApiErrorMessage(response, "Failed to cancel session"));
      }
    } catch (e) {
      // Cancel is a chat-flow action, so surface the failure via sendError (the composer-area
      // banner) to match startChat/sendMessage, not the session-level `error` channel. Return to
      // 'ready' so the user can retry the stop.
      _clearCancelSafety();
      _markChatStatusReason('cancelSession:error');
      set({ chatStatus: 'ready', sendError: resolveApiErrorMessage(e, "Failed to cancel session") });
    }
  },

  stopManagedRun: async (chatSessionId, reason = "Stopped by user") => {
    try {
      const response = await stopManagedRunRequest(chatSessionId, reason);
      const payload = response?.data || {};
      set((s) => {
        if (s.sessionDetail?.chatSessionId !== chatSessionId) return {};
        return {
          sessionDetail: {
            ...s.sessionDetail,
            managedRunStatus: payload.managedRunStatus || "STOPPED",
          },
        };
      });
      get().refreshSessionInList(chatSessionId);
      return payload;
    } catch (e) {
      set({ error: resolveApiErrorMessage(e, "Failed to stop managed run") });
      return null;
    }
  },

  refreshSessionInList: (chatSessionId) => {
    set((s) => {
      const detail = s.sessionDetail;
      if (!detail || detail.chatSessionId !== chatSessionId) return {};
      return {
        sessions: applySessionDetailToList(s.sessions, detail, {
          clearOptimistic: true,
        }),
      };
    });
  },

  deleteAgent: async (agentId) => {
    try {
      await send_request(`project-service/agent/${agentId}`, "", {}, "DELETE");
      set((s) => ({
        agents: s.agents.filter((a) => a.agentId !== agentId),
      }));
      return true;
    } catch (e) {
      set({ error: e.message || "Failed to delete agent" });
      return false;
    }
  },

  saveEditedAgentFile: async ({
    agentId,
    previousFileId,
    fileName,
    content,
    mimeType,
  }) => {
    const normalizedAgentId = String(agentId || "").trim();
    const normalizedFileName = String(fileName || "").trim() || "document.txt";
    const normalizedMimeType =
      String(mimeType || "").trim() ||
      inferMimeTypeFromFileName(normalizedFileName);

    if (!normalizedAgentId) {
      const message = "Missing agent ID for file save";
      set({ error: message });
      throw new Error(message);
    }

    const formData = new FormData();
    formData.append(
      "file",
      new Blob([content ?? ""], { type: normalizedMimeType }),
      normalizedFileName
    );
    formData.append("configId", "fileupload");
    formData.append("type", "upload");
    formData.append("projectId", "");
    formData.append("projectSessionId", "");
    formData.append("formSessionId", "");
    formData.append("agentId", normalizedAgentId);

    try {
      const uploadResponse = await send_request(
        "project-service/project/file-library/create",
        formData,
        "",
        "POST",
        { "content-type": "multipart/form-data" }
      );

      const uploaded = uploadResponse?.data || null;
      const uploadedFileId = String(uploaded?.id || "").trim();
      if (!uploadedFileId) {
        const message = resolveApiErrorMessage(
          uploadResponse,
          "Failed to save edited file"
        );
        set({ error: message });
        throw new Error(message);
      }

      let warning = "";
      const normalizedPreviousId = String(previousFileId || "").trim();
      if (normalizedPreviousId && normalizedPreviousId !== uploadedFileId) {
        const deleteResponse = await send_request(
          `project-service/project/file-library/agent/${normalizedAgentId}/deletes`,
          { deleteList: [normalizedPreviousId] },
          "",
          "POST"
        );

        const deleteFailed = extractApiStatusCode(deleteResponse) >= 400;
        if (deleteFailed) {
          warning = resolveApiErrorMessage(
            deleteResponse,
            "Updated file saved, but old file deletion failed"
          );
        }
      }

      return {
        fileId: uploadedFileId,
        fileName: uploaded?.fileName || normalizedFileName,
        downloadUrl: `${config.FILE_SERVICE.QUERY_URL}/${uploadedFileId}`,
        mimeType:
          uploaded?.mimeType || uploaded?.fileType || normalizedMimeType,
        size: uploaded?.fileSize ?? null,
        createdAt: uploaded?.createDate || null,
        agentId: normalizedAgentId,
        warning,
      };
    } catch (e) {
      const message = e?.message || "Failed to save edited file";
      set({ error: message });
      throw e instanceof Error ? e : new Error(message);
    }
  },

  uploadChatAttachment: async (file, agentId) => {
    const normalizedAgentId = String(agentId || "").trim();
    if (!normalizedAgentId) throw new Error("Missing agent ID for file upload");

    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("configId", "fileupload");
    formData.append("type", "upload");
    formData.append("projectId", "");
    formData.append("projectSessionId", "");
    formData.append("formSessionId", "");
    formData.append("agentId", normalizedAgentId);

    const uploadResponse = await send_request(
      "project-service/project/file-library/create",
      formData,
      "",
      "POST",
      { "content-type": "multipart/form-data" }
    );

    const uploaded = uploadResponse?.data || null;
    const uploadedFileId = String(uploaded?.id || "").trim();
    if (!uploadedFileId) {
      throw new Error("Failed to upload file");
    }

    return {
      fileId: uploadedFileId,
      fileName: uploaded?.fileName || file.name,
      downloadUrl: `${config.FILE_SERVICE.QUERY_URL}/${uploadedFileId}`,
      mimeType: uploaded?.mimeType || uploaded?.fileType || file.type,
      size: uploaded?.fileSize ?? file.size,
    };
  },

  uploadAgentAvatar: async (agentId, file) => {
    const formData = new FormData();
    formData.append("file", file, file.name);
    return _applyAgentAvatarMutation(set, {
      agentId,
      body: formData,
      method: "POST",
      headers: { "content-type": "multipart/form-data" },
      missingIdMessage: "Missing agent ID for avatar upload",
      failureMessage: "Failed to upload avatar",
    });
  },

  clearAgentAvatar: async (agentId) => {
    return _applyAgentAvatarMutation(set, {
      agentId,
      body: {},
      method: "DELETE",
      missingIdMessage: "Missing agent ID for avatar removal",
      failureMessage: "Failed to remove avatar",
    });
  },

  deleteChatAttachment: async (fileId, agentId) => {
    const normalizedAgentId = String(agentId || "").trim();
    const normalizedFileId = String(fileId || "").trim();
    if (!normalizedAgentId || !normalizedFileId) return;

    await send_request(
      `project-service/project/file-library/agent/${normalizedAgentId}/deletes`,
      { deleteList: [normalizedFileId] },
      "",
      "POST"
    );
  },
}));

// The live-stream controller owns the reducer cursors, the intra-frame flush
// buffer, and the animation-frame throttle for SSE ui_chunk events. It is wired
// with the store accessors and the store-owned helpers it must call back into.
const streamController = createStreamController({
  getState: () => useAgentStore.getState(),
  setState: (patch) => useAgentStore.setState(patch),
  isTerminalForStreaming: _isTerminalForStreaming,
  clearSubmittedRecovery: _clearSubmittedRecovery,
  markChatStatusReason: _markChatStatusReason,
  isEmptyAssistantStreamMessage: _isEmptyAssistantStreamMessage,
});

// Always-on logging of chatStatus transitions. Key signal for diagnosing
// "chat stuck on in_progress" — tells you exactly when (and if) the UI
// moved submitted → streaming → ready, or got stuck.
//
// Guarded behind a window-scoped flag so HMR / double module evaluation
// doesn't register duplicate subscribers (which would emit each transition
// log N times).
const _CHAT_STATUS_SUBSCRIPTION_KEY = '__useAgentStoreChatStatusSubscription__';
if (typeof window !== 'undefined' && !window[_CHAT_STATUS_SUBSCRIPTION_KEY]) {
  let _prevChatStatus = useAgentStore.getState().chatStatus;
  window[_CHAT_STATUS_SUBSCRIPTION_KEY] = useAgentStore.subscribe((state) => {
    const next = state.chatStatus;
    if (next !== _prevChatStatus) {
      const level = next === 'error' ? 'error' : 'info';
      const reason = _chatStatusReason || 'unknown';
      _crit("AgentStore", level, `chatStatus ${_prevChatStatus} → ${next} (reason=${reason})`);

      if (next === 'streaming') {
        const streamState = useChatStreamStore.getState().sessionStreamStateBySessionId;
        const sid = state.sessionDetail?.chatSessionId;
        const cs = sid ? streamState[sid] : null;
        if (cs !== 'connected' && cs !== 'connecting') {
          _crit(
            "AgentStore",
            "error",
            `PHANTOM STREAMING — chatStatus=streaming but SSE is ${cs || 'idle'} for session=${sid}. reason=${reason}`
          );
        }
      }

      if (next === 'ready') {
        const sid = state.sessionDetail?.chatSessionId;
        if (sid) {
          _auditUnresolvedStreamState(() => useAgentStore.getState(), sid, `chatStatus→ready:${reason}`);
        }
      }

      _prevChatStatus = next;
      _chatStatusReason = null;
    }
  });
}

export default useAgentStore;
